import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { BooleanInput } from 'ng-zorro-antd/core/types';
import { InputBoolean } from 'ng-zorro-antd/core/util';
import { MFileItemComponent } from './file-item.component';
import { IMFileItem, IMStatus } from './typings';

@Component({
  selector: 'm-file-display',
  template: `
    <div class="m-file-display-container">
      <div class="m-file-display-header">
        <ng-container *ngIf="mShowCheckBox; else elseTemplate">
          <label nz-checkbox [(ngModel)]="allChecked" (ngModelChange)="onAllChecked($event)">全部文件</label>
          <span class="m-file-display-clear" (click)="clear()">删除选中 ({{ setOfCheckedId.size }})</span>
        </ng-container>
        <ng-template #elseTemplate>
          <span>全部文件</span>
        </ng-template>
      </div>
      <div class="m-file-display-body">
        <ng-container *ngIf="currentFileList.length; else emptyTemplate">
          <m-file-display-item
            *ngFor="let item of currentFileList"
            [mName]="item.name"
            [mStatus]="item.status"
            [mPercent]="item.percent"
            [mSize]="item.size"
            [mUid]="item.uid"
            [mShowCheckBox]="mShowCheckBox"
            [mEditable]="item.editable || false"
            [mMismatch]="item.mismatch || false"
            [mChecked]="item.checked"
            (mDeleteItem)="deleteFileItem($event)"
            (mEditChange)="editChange($event)"
            (mSubmit)="passwordChange($event)"
            (mCheckedChange)="onItemChecked($event)"
            (mouseEnter)="onMouseEnter($event)"
            (mouseLeave)="onMouseLeave($event)"
          ></m-file-display-item>
        </ng-container>
        <ng-template #emptyTemplate>
          <nz-empty></nz-empty>
        </ng-template>
      </div>
      <div class="m-file-display-footer">
        <ng-container [ngTemplateOutlet]="mFooterRef"></ng-container>
      </div>
    </div>
  `,
  host: {
    class: 'm-file-display'
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MFileDisplayComponent implements OnChanges {
  static ngAcceptInputType_mShowCheckBox: BooleanInput;

  @Input() mFileList: IMFileItem[] = [];
  @Input() @InputBoolean() mShowCheckBox: boolean = false;
  @Input() mFooterRef: TemplateRef<void> | null = null;

  @Output() readonly mFileListChange = new EventEmitter<IMFileItem[]>();
  @Output() readonly mPasswordChange = new EventEmitter<{ uid: string; password: string }>();
  @Output() readonly mouseEnter = new EventEmitter<{ status: IMStatus; typeIcon: HTMLElement; closeIcon: HTMLElement }>();
  @Output() readonly mouseLeave = new EventEmitter<{ status: IMStatus; typeIcon: HTMLElement; closeIcon: HTMLElement }>();

  allChecked = true;
  currentFileList: IMFileItem[] = [];
  setOfCheckedId = new Set<string>();
  @ViewChildren(MFileItemComponent) fileItems!: QueryList<MFileItemComponent>;

  ngOnChanges(changes: SimpleChanges): void {
    const { mFileList } = changes;
    if (mFileList) {
      this.currentFileList = [...mFileList.currentValue];
      this.currentFileList.forEach(item => {
        if (item.checked) {
          this.setOfCheckedId.add(item.uid);
        }
      });
      this.refreshCheckedStatus();
    }
  }

  deleteFileItem(uid: string): void {
    this.currentFileList = this.currentFileList.filter(ele => ele.uid !== uid);
    this.setOfCheckedId.delete(uid);
    this.mFileListChange.emit(this.currentFileList);
  }

  editChange(value: { uid: string; editable: boolean }): void {
    this.currentFileList.forEach(item => (item.editable = false));
    this.currentFileList.find(item => item.uid === value.uid)!.editable = value.editable;
  }

  passwordChange(value: { uid: string; password: string }): void {
    this.mPasswordChange.emit({ uid: value.uid, password: value.password });
  }

  onItemChecked(value: { uid: string; checked: boolean }): void {
    this.updateCheckedSet(value.uid, value.checked);
    this.refreshCheckedStatus();
    this.mFileListChange.emit(this.currentFileList);
  }

  onAllChecked(value: boolean): void {
    this.currentFileList.forEach(item => this.updateCheckedSet(item.uid, value));
    this.refreshCheckedStatus();
    this.mFileListChange.emit(this.currentFileList);
  }

  updateCheckedSet(uid: string, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(uid);
    } else {
      this.setOfCheckedId.delete(uid);
    }
    this.currentFileList.find(item => item.uid === uid)!.checked = checked;
  }

  refreshCheckedStatus(): void {
    this.allChecked = this.currentFileList.every(item => this.setOfCheckedId.has(item.uid));
  }

  clear(): void {
    if (this.setOfCheckedId.size) {
      this.currentFileList = this.currentFileList.filter(ele => !this.setOfCheckedId.has(ele.uid));
      this.setOfCheckedId.clear();
      this.mFileListChange.emit(this.currentFileList);
    }
  }

  onMouseEnter(ele: { status: IMStatus; typeIcon: HTMLElement; closeIcon: HTMLElement }): void {
    if (ele.closeIcon || ele.typeIcon) {
      this.mouseEnter.emit({ status: ele.status, typeIcon: ele.typeIcon, closeIcon: ele.closeIcon });
    }
  }

  onMouseLeave(ele: { status: IMStatus; typeIcon: HTMLElement; closeIcon: HTMLElement }): void {
    if (ele.closeIcon || ele.typeIcon) {
      this.mouseLeave.emit({ status: ele.status, typeIcon: ele.typeIcon, closeIcon: ele.closeIcon });
    }
  }

  checkChildren(): void {
    if (this.fileItems) {
      this.fileItems.forEach(item => item.markForCheck());
    }
  }
}
